import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Highlight, themes, Prism } from 'prism-react-renderer';
import { Button } from '../Button/Button.es.js';
import { Icon } from '../Icon/Icon.es.js';

var SupportedLanguages;
(function(SupportedLanguages) {
    SupportedLanguages["JavaScript"] = "javascript";
    SupportedLanguages["JSON"] = "json";
    SupportedLanguages["JSX"] = "jsx";
    SupportedLanguages["Python"] = "python";
    SupportedLanguages["TSX"] = "tsx";
    SupportedLanguages["TypeScript"] = "typescript";
    SupportedLanguages["Bash"] = "bash";
})(SupportedLanguages || (SupportedLanguages = {}));
// Reset the copied icon state after 3 seconds.
const COPY_CLIPBOARD_RESET_INTERVAL = 3000;
/**
 * Component to render a code block with syntax highlight.
 * This component will render the code content as-is, and will not perform any code formatting.
 */ const CodeSnippet = ({ content, language, className, variant = 'default', numbers = true, lines = Number.MAX_VALUE, copiable = false, expanded = false, locale = {
    showLess: 'Show Less',
    showMore: 'Show More',
    ariaLabel: 'Copy Text'
} })=>{
    const [condensed, setCondensed] = useState(!expanded);
    const [isCheckIconShowing, setIsCheckIconShowing] = useState(false);
    useEffect(()=>{
        // Clear the interval when the component unmounts and value was copied
        if (isCheckIconShowing) {
            const timeout = setTimeout(()=>setIsCheckIconShowing(false), COPY_CLIPBOARD_RESET_INTERVAL);
            return ()=>clearTimeout(timeout);
        }
    }, [
        isCheckIconShowing
    ]);
    const handleCopyToClipboard = useCallback(()=>{
        void navigator.clipboard.writeText(content);
        setIsCheckIconShowing(true);
    }, [
        content
    ]);
    const handleShowClick = useCallback(()=>{
        setCondensed((prev)=>!prev);
    }, []);
    return jsx("div", {
        className: classNames('cb-code-snippet', {
            boxed: variant === 'boxed'
        }, className),
        "data-testid": "code-snippet",
        children: jsx(Highlight, {
            code: content,
            language: language,
            prism: Prism,
            theme: themes.vsLight,
            children: ({ className, style, tokens, getLineProps, getTokenProps })=>jsxs("pre", {
                    className: classNames(className),
                    style: style,
                    children: [
                        jsxs("span", {
                            className: "code-box",
                            children: [
                                jsx("code", {
                                    children: tokens.flatMap((line, i)=>!condensed || i < lines ? jsxs("div", {
                                            ...getLineProps({
                                                line
                                            }),
                                            children: [
                                                numbers && jsx("span", {
                                                    className: "line-number",
                                                    "data-testid": i,
                                                    children: i + 1
                                                }),
                                                jsx("span", {
                                                    children: line.map((token, key)=>jsx("span", {
                                                            ...getTokenProps({
                                                                token
                                                            })
                                                        }, key))
                                                })
                                            ]
                                        }, i) : [])
                                }),
                                copiable && jsx(Button, {
                                    label: locale.ariaLabel,
                                    onClick: handleCopyToClipboard,
                                    variant: "secondary",
                                    iconOnly: true,
                                    children: jsx(Icon, {
                                        name: isCheckIconShowing ? 'CheckOutline' : 'DuplicateSolid',
                                        size: 24
                                    })
                                })
                            ]
                        }),
                        lines < tokens.length && jsx(Button, {
                            onClick: handleShowClick,
                            variant: "text",
                            children: jsxs(Fragment, {
                                children: [
                                    condensed ? locale.showMore : locale.showLess,
                                    jsx(Icon, {
                                        name: condensed ? 'ChevronDownSolid' : 'ChevronUpSolid',
                                        size: 14
                                    })
                                ]
                            })
                        })
                    ]
                })
        })
    });
};
CodeSnippet.displayName = 'CodeSnippet';

export { CodeSnippet, SupportedLanguages, CodeSnippet as default };
//# sourceMappingURL=CodeSnippet.es.js.map
